/**
 * Low-level Grafana datasource-proxy client (ClickUp 86e1w8ude, phase 1).
 *
 * Queries Prometheus + Loki through Grafana's datasource-proxy API:
 *   Prometheus  GET {base}/api/datasources/proxy/uid/{promUid}/api/v1/query
 *   Loki        GET {base}/api/datasources/proxy/uid/{lokiUid}/loki/api/v1/query_range
 *
 * Phase 1 talks to Grafana DIRECTLY from the browser with a service-account
 * Bearer token (see config.grafana for the caveats). This file is the single
 * place that knows the wire format — swap `config.grafana.base` to '' and front
 * it with a worker route in phase 2 without touching callers.
 */
import { config } from '@/lib/config';

const TIMEOUT_MS = 12_000;

function promBase(): string {
  return `${config.grafana.base}/api/datasources/proxy/uid/${config.grafana.promUid}/api/v1`;
}
function lokiBase(): string {
  return `${config.grafana.base}/api/datasources/proxy/uid/${config.grafana.lokiUid}/loki/api/v1`;
}
function authHeaders(): HeadersInit {
  return config.grafana.token ? { Authorization: `Bearer ${config.grafana.token}` } : {};
}

// Auth is injected by the same-origin proxy (worker.ts / vite `/grafana`), so
// readiness only needs the base + datasource UID. A client-side token is the
// optional direct-mode fallback (sent via authHeaders below).
/** True when enough config exists to attempt a Prometheus query. */
export function grafanaPromReady(): boolean {
  return !!config.grafana.base && !!config.grafana.promUid;
}
/** True when enough config exists to attempt a Loki query. */
export function grafanaLokiReady(): boolean {
  return !!config.grafana.base && !!config.grafana.lokiUid;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Grafana ${res.status} ${res.statusText}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/* ── Prometheus ─────────────────────────────────────────────────────────── */

export type PromSample = { metric: Record<string, string>; value: [number, string] };
type PromInstantResponse = {
  status: 'success' | 'error';
  data?: { resultType: string; result: PromSample[] };
  error?: string;
};

/** Run an instant PromQL query; returns the raw vector samples. */
export async function promInstant(query: string): Promise<PromSample[]> {
  const url = `${promBase()}/query?query=${encodeURIComponent(query)}`;
  const json = await getJson<PromInstantResponse>(url);
  if (json.status !== 'success' || !json.data) {
    throw new Error(`PromQL failed: ${json.error ?? 'unknown'} — ${query.slice(0, 120)}`);
  }
  return json.data.result;
}

/** Run many instant queries in parallel, preserving input order. */
export function promInstantAll(queries: string[]): Promise<PromSample[][]> {
  return Promise.all(queries.map(promInstant));
}

export type PromMatrix = { metric: Record<string, string>; values: [number, string][] };
type PromRangeResponse = {
  status: 'success' | 'error';
  data?: { resultType: string; result: PromMatrix[] };
  error?: string;
};

/**
 * Run a PromQL range query. `startMs`/`endMs` are epoch-ms; `stepS` is the
 * resolution in seconds. Returns the raw matrix (one entry per series).
 */
export async function promRange(
  query: string,
  startMs: number,
  endMs: number,
  stepS: number,
): Promise<PromMatrix[]> {
  const params = new URLSearchParams({
    query,
    start: String(Math.floor(startMs / 1000)),
    end: String(Math.floor(endMs / 1000)),
    step: String(Math.max(1, Math.round(stepS))),
  });
  const json = await getJson<PromRangeResponse>(`${promBase()}/query_range?${params.toString()}`);
  if (json.status !== 'success' || !json.data) {
    throw new Error(`PromQL range failed: ${json.error ?? 'unknown'} — ${query.slice(0, 120)}`);
  }
  return json.data.result;
}

/** Run many range queries in parallel, preserving input order. */
export function promRangeAll(
  queries: string[],
  startMs: number,
  endMs: number,
  stepS: number,
): Promise<PromMatrix[][]> {
  return Promise.all(queries.map((q) => promRange(q, startMs, endMs, stepS)));
}

/* ── Loki ───────────────────────────────────────────────────────────────── */

export type LokiStream = { stream: Record<string, string>; values: [string, string][] };
type LokiResponse = {
  status: 'success' | 'error';
  data?: { resultType: string; result: LokiStream[] };
};

/**
 * Loki range query. `start`/`end` are JS epoch-ms; converted to the
 * nanosecond strings Loki expects (BigInt — ms*1e6 overflows Number).
 */
export async function lokiRange(
  logql: string,
  startMs: number,
  endMs: number,
  limit = 100,
  direction: 'backward' | 'forward' = 'backward',
): Promise<LokiStream[]> {
  const startNs = (BigInt(Math.floor(startMs)) * 1_000_000n).toString();
  const endNs = (BigInt(Math.floor(endMs)) * 1_000_000n).toString();
  const params = new URLSearchParams({
    query: logql,
    start: startNs,
    end: endNs,
    limit: String(limit),
    direction,
  });
  const json = await getJson<LokiResponse>(`${lokiBase()}/query_range?${params.toString()}`);
  if (json.status !== 'success' || !json.data) {
    throw new Error(`LogQL failed — ${logql.slice(0, 120)}`);
  }
  return json.data.result;
}
